import { useState, useMemo, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { db } from "../lib/db";
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const dAgo = (days) => { const d = new Date(); d.setDate(d.getDate() - days); return d.toISOString(); };
const fmt  = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const calcAge = (dob) => { if (!dob) return "?"; const b = new Date(dob), n = new Date(); return n.getFullYear() - b.getFullYear() - (n < new Date(n.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0); };
const formatDOB = (dob) => { if (!dob) return ""; const [y,m,d] = dob.split("-"); return `${m}/${d}/${y}`; };

// ─── FEE SCHEDULE DATA ────────────────────────────────────────────────────────
const MEDICARE_CF = 33.89;
const SCHEDULE_MULT  = { medicare: 1.00, commercial_bcbs: 1.20, commercial_aetna: 1.15, commercial_uhc: 1.12, commercial_cigna: 1.10, medicaid: 0.75, workers_comp: 1.15, pi_lien: 1.40, self_pay: 15.0 };
const FACILITY_MULT  = { asc: 0.85, hospital_outpatient: 1.40, hospital_inpatient: 2.20 };
const FACILITY_LABEL = { asc: "ASC", hospital_outpatient: "Hospital Outpatient", hospital_inpatient: "Hospital Inpatient" };

const FEE_SCHEDULES = {
  medicare:         { label: "Medicare",                  color: "#3b82f6" },
  commercial_bcbs:  { label: "BCBS",                      color: "#0ea5a0" },
  commercial_aetna: { label: "Aetna",                     color: "#ec4899" },
  commercial_uhc:   { label: "UnitedHealthcare",           color: "#8b5cf6" },
  commercial_cigna: { label: "Cigna",                     color: "#f97316" },
  medicaid:         { label: "Medicaid/AHCCCS",            color: "#6b7280" },
  workers_comp:     { label: "Workers Comp (AZ)",          color: "#f59e0b" },
  pi_lien:          { label: "PI Lien",                    color: "#ef4444" },
  self_pay:         { label: "Self Pay (Chargemaster)",    color: "#94a3b8" },
};

const FACILITIES = {
  asc:                ["Scottsdale Surgical Center", "Arizona Ambulatory Surgery Center", "OrthoArizona ASC"],
  hospital_outpatient:["Mayo Clinic Scottsdale", "Banner Desert Medical Center", "HonorHealth Scottsdale"],
  hospital_inpatient: ["Mayo Clinic Scottsdale", "Banner Desert Medical Center", "HonorHealth Scottsdale"],
};

const IMPLANTS = [
  { name: "None",                                cost: 0,     note: "No implant required" },
  { name: "Carpal Tunnel Release",               cost: 0,     note: "No implant" },
  { name: "Trigger Finger Release",              cost: 0,     note: "No implant" },
  { name: "CMC Arthroplasty — Suture Anchor",    cost: 300,   note: "Range: $200–$400" },
  { name: "Tendon Transfer Anchors",             cost: 600,   note: "Range: $400–$800" },
  { name: "Distal Radius Plate & Screws",        cost: 1500,  note: "Range: $1,200–$1,800" },
  { name: "Wrist Fusion Hardware",               cost: 2100,  note: "Range: $1,800–$2,400" },
  { name: "Proximal Humerus Plate",              cost: 2800,  note: "Range: $2,400–$3,200" },
  { name: "Elbow Replacement",                   cost: 7500,  note: "Range: $6,000–$9,000" },
  { name: "Shoulder Arthroplasty Implant",       cost: 10000, note: "Range: $8,000–$12,000" },
  { name: "Custom",                              cost: 0,     note: "Enter cost manually" },
];

// ─── CPT LIBRARY WITH RVUs ────────────────────────────────────────────────────
const CPT_LIBRARY = [
  // Surgical
  { code: "29827", desc: "Arthroscopy, shoulder, w/rotator cuff repair",           rvu: 18.16 },
  { code: "64721", desc: "Carpal tunnel release, open",                              rvu: 7.76  },
  { code: "26055", desc: "Trigger finger release, tendon sheath",                    rvu: 4.55  },
  { code: "25600", desc: "Closed tx distal radius fracture",                         rvu: 5.57  },
  { code: "25607", desc: "ORIF distal radius, external fixation",                    rvu: 16.62 },
  { code: "25609", desc: "ORIF distal radius, internal fixation",                    rvu: 18.22 },
  { code: "23472", desc: "Total shoulder arthroplasty",                              rvu: 27.61 },
  { code: "23470", desc: "Hemiarthroplasty, shoulder",                               rvu: 22.18 },
  { code: "26515", desc: "Arthroplasty, CMC joint, thumb (LRTI)",                    rvu: 14.21 },
  { code: "64722", desc: "Decompression, ulnar nerve at elbow",                      rvu: 8.54  },
  { code: "24357", desc: "Lateral epicondylitis (tennis elbow) release",             rvu: 8.07  },
  { code: "25800", desc: "Arthrodesis, wrist joint, complete",                       rvu: 17.45 },
  { code: "24365", desc: "Arthroplasty, humeral head with prosthesis",               rvu: 20.45 },
  { code: "29848", desc: "Arthroscopy, wrist, surgical",                             rvu: 8.93  },
  { code: "26160", desc: "Excision of lesion, tendon sheath, finger",                rvu: 5.06  },
  { code: "26040", desc: "Fasciotomy, palmar, open, partial",                        rvu: 5.36  },
  { code: "26540", desc: "Repair, collateral ligament, metacarpophalangeal joint",   rvu: 9.88  },
  { code: "26536", desc: "Arthroplasty, MCP joint with prosthesis",                  rvu: 11.44 },
  { code: "20680", desc: "Removal of implant, deep",                                 rvu: 7.45  },
  { code: "25652", desc: "ORIF ulnar styloid fracture",                              rvu: 12.88 },
  { code: "26600", desc: "Closed tx metacarpal fracture, each bone",                 rvu: 3.81  },
  // E&M / Office
  { code: "99213", desc: "Office visit, established patient, low complexity",        rvu: 1.30  },
  { code: "99214", desc: "Office visit, established patient, moderate complexity",   rvu: 1.92  },
  { code: "99215", desc: "Office visit, established patient, high complexity",       rvu: 2.80  },
  { code: "99202", desc: "Office visit, new patient, low complexity",                rvu: 1.61  },
  { code: "99203", desc: "Office visit, new patient, moderate complexity",           rvu: 2.60  },
  { code: "99204", desc: "Office visit, new patient, moderate complexity (detailed)",rvu: 3.50  },
  // Injections / Procedures
  { code: "20600", desc: "Arthrocentesis, small joint",                              rvu: 0.62  },
  { code: "20605", desc: "Arthrocentesis, intermediate joint",                       rvu: 0.70  },
  { code: "20610", desc: "Arthrocentesis, major joint",                              rvu: 0.89  },
  { code: "20550", desc: "Injection, tendon sheath or ligament",                     rvu: 0.80  },
  { code: "20551", desc: "Injection, tendon origin/insertion",                       rvu: 0.78  },
  // Fracture care
  { code: "25600", desc: "Closed tx distal radial fracture",                         rvu: 5.57  },
  { code: "26720", desc: "Closed tx phalangeal shaft fracture, each",                rvu: 2.99  },
  { code: "27447", desc: "Total knee arthroplasty",                                  rvu: 29.14 },
  { code: "27130", desc: "Total hip arthroplasty",                                   rvu: 28.92 },
];

// ─── PAYER MAPPING ─────────────────────────────────────────────────────────────
const payerToSchedule = (ins) => {
  const p = (ins || "").toLowerCase();
  if (p.includes("blue cross") || p.includes("bcbs")) return "commercial_bcbs";
  if (p.includes("aetna")) return "commercial_aetna";
  if (p.includes("united") || p.includes("uhc")) return "commercial_uhc";
  if (p.includes("cigna")) return "commercial_cigna";
  if (p.includes("medicare")) return "medicare";
  if (p.includes("medicaid") || p.includes("ahcccs")) return "medicaid";
  if (p.includes("workers") || p.includes("comp")) return "workers_comp";
  return "commercial_bcbs";
};
const defaultInsParams = (sched) => ({
  medicare:         { deductible: 226,  coinsurance: 20, copay: 0,  oopMax: 999999 },
  commercial_bcbs:  { deductible: 1500, coinsurance: 20, copay: 40, oopMax: 5000   },
  commercial_aetna: { deductible: 2000, coinsurance: 20, copay: 30, oopMax: 6000   },
  commercial_uhc:   { deductible: 1750, coinsurance: 20, copay: 50, oopMax: 7000   },
  commercial_cigna: { deductible: 2000, coinsurance: 20, copay: 35, oopMax: 6500   },
  medicaid:         { deductible: 0,    coinsurance: 0,  copay: 3,  oopMax: 999999 },
  workers_comp:     { deductible: 0,    coinsurance: 0,  copay: 0,  oopMax: 999999 },
  pi_lien:          { deductible: 0,    coinsurance: 0,  copay: 0,  oopMax: 999999 },
  self_pay:         { deductible: 0,    coinsurance: 0,  copay: 0,  oopMax: 999999 },
}[sched] || { deductible: 1500, coinsurance: 20, copay: 40, oopMax: 5000 });

const getDefaultForm = (patient) => {
  const sched = payerToSchedule(patient?.insurance || "");
  const ins = defaultInsParams(sched);
  return {
    procedureName: "", selectedCpts: [],
    facilityType: "asc", facilityName: "Scottsdale Surgical Center",
    feeSchedule: sched,
    hasImplant: false, implantType: "None", implantCost: 0,
    hasAnesthesia: true, anesthesiaType: "general",
    baseUnits: 7, procedureDuration: 60, anesthesiaRate: 85,
    deductibleRemaining: ins.deductible, coinsurance: ins.coinsurance,
    copay: ins.copay, oopMaxRemaining: ins.oopMax,
    payer: patient?.insurance || "",
  };
};

// ─── MOCK DATA ─────────────────────────────────────────────────────────────────
export const MOCK_COST_ESTIMATES = [
  { id:"CE001", patient_id:"P001", provider:"Dr. R. Walker", procedure_name:"Arthroscopic Rotator Cuff Repair, Right Shoulder",   cpt_codes:[{code:"29827",desc:"Arthroscopy shoulder w/rotator cuff repair",rvu:18.16}],  facility_type:"asc",                facility_name:"Scottsdale Surgical Center",    payer:"Blue Cross Blue Shield",     fee_schedule_type:"commercial_bcbs",  professional_fee:923,  facility_fee:816,  anesthesia_fee:1105, implant_cost:0,    total_billed:2844, payer_allowed:2471, patient_deductible_remaining:1500, patient_coinsurance:20, patient_copay:40, patient_total_oop:1734, insurance_pays:737,  created_at:dAgo(15), quote_sent:true  },
  { id:"CE002", patient_id:"P001", provider:"Dr. R. Walker", procedure_name:"Office E&M Follow-up, Post-Operative",               cpt_codes:[{code:"99214",desc:"Office visit, moderate complexity",rvu:1.92}],            facility_type:"asc",                facility_name:"",                             payer:"Blue Cross Blue Shield",     fee_schedule_type:"commercial_bcbs",  professional_fee:98,   facility_fee:0,    anesthesia_fee:0,    implant_cost:0,    total_billed:98,   payer_allowed:78,   patient_deductible_remaining:0,    patient_coinsurance:20, patient_copay:40, patient_total_oop:40,   insurance_pays:38,   created_at:dAgo(10), quote_sent:false },
  { id:"CE003", patient_id:"P002", provider:"Dr. R. Walker", procedure_name:"ORIF Distal Radius Fracture, Right",                 cpt_codes:[{code:"25607",desc:"ORIF distal radius, external fixation",rvu:16.62}],      facility_type:"hospital_outpatient", facility_name:"Banner Desert Medical Center", payer:"Aetna",                      fee_schedule_type:"commercial_aetna", professional_fee:810,  facility_fee:1179, anesthesia_fee:1326, implant_cost:1500, total_billed:4815, payer_allowed:3775, patient_deductible_remaining:2000, patient_coinsurance:20, patient_copay:30, patient_total_oop:2355, insurance_pays:1420, created_at:dAgo(30), quote_sent:true  },
  { id:"CE004", patient_id:"P002", provider:"Dr. R. Walker", procedure_name:"Carpal Tunnel Release, Right",                       cpt_codes:[{code:"64721",desc:"Carpal tunnel release, open",rvu:7.76}],                 facility_type:"asc",                facility_name:"Arizona Ambulatory Surgery Center", payer:"Aetna",             fee_schedule_type:"commercial_aetna", professional_fee:378,  facility_fee:334,  anesthesia_fee:765,  implant_cost:0,    total_billed:1477, payer_allowed:1324, patient_deductible_remaining:0,    patient_coinsurance:20, patient_copay:30, patient_total_oop:295,  insurance_pays:1029, created_at:dAgo(5),  quote_sent:false },
  { id:"CE005", patient_id:"P003", provider:"Dr. R. Walker", procedure_name:"Trigger Finger Release, Right Ring Finger",          cpt_codes:[{code:"26055",desc:"Trigger finger release, tendon sheath",rvu:4.55}],       facility_type:"asc",                facility_name:"OrthoArizona ASC",              payer:"UnitedHealthcare",           fee_schedule_type:"commercial_uhc",   professional_fee:216,  facility_fee:184,  anesthesia_fee:612,  implant_cost:0,    total_billed:1012, payer_allowed:812,  patient_deductible_remaining:1750, patient_coinsurance:20, patient_copay:50, patient_total_oop:812,  insurance_pays:0,    created_at:dAgo(20), quote_sent:true  },
  { id:"CE006", patient_id:"P003", provider:"Dr. R. Walker", procedure_name:"Ulnar Nerve Decompression at Elbow",                 cpt_codes:[{code:"64722",desc:"Decompression, ulnar nerve at elbow",rvu:8.54}],        facility_type:"asc",                facility_name:"OrthoArizona ASC",              payer:"UnitedHealthcare",           fee_schedule_type:"commercial_uhc",   professional_fee:323,  facility_fee:275,  anesthesia_fee:765,  implant_cost:0,    total_billed:1363, payer_allowed:1078, patient_deductible_remaining:938,  patient_coinsurance:20, patient_copay:50, patient_total_oop:1066, insurance_pays:12,   created_at:dAgo(8),  quote_sent:false },
  { id:"CE007", patient_id:"P004", provider:"Dr. R. Walker", procedure_name:"CMC Arthroplasty, Right Thumb (LRTI)",               cpt_codes:[{code:"26515",desc:"Arthroplasty, CMC joint, thumb (LRTI)",rvu:14.21}],     facility_type:"asc",                facility_name:"Scottsdale Surgical Center",    payer:"Medicare",                   fee_schedule_type:"medicare",         professional_fee:481,  facility_fee:409,  anesthesia_fee:935,  implant_cost:300,  total_billed:2125, payer_allowed:1588, patient_deductible_remaining:226,  patient_coinsurance:20, patient_copay:0,  patient_total_oop:519,  insurance_pays:1069, created_at:dAgo(45), quote_sent:true  },
  { id:"CE008", patient_id:"P004", provider:"Dr. R. Walker", procedure_name:"Corticosteroid Injection, CMC Joint",                cpt_codes:[{code:"20600",desc:"Arthrocentesis, small joint",rvu:0.62}],                facility_type:"asc",                facility_name:"",                             payer:"Medicare",                   fee_schedule_type:"medicare",         professional_fee:21,   facility_fee:0,    anesthesia_fee:0,    implant_cost:0,    total_billed:21,   payer_allowed:21,   patient_deductible_remaining:0,    patient_coinsurance:20, patient_copay:0,  patient_total_oop:4,    insurance_pays:17,   created_at:dAgo(60), quote_sent:false },
  { id:"CE009", patient_id:"P005", provider:"Dr. R. Walker", procedure_name:"UCL Repair, Right Thumb (Workers Compensation)",     cpt_codes:[{code:"26540",desc:"Repair, collateral ligament, metacarpal joint",rvu:9.88}], facility_type:"hospital_outpatient", facility_name:"Banner Desert Medical Center", payer:"Arizona State Fund (WC)", fee_schedule_type:"workers_comp",     professional_fee:385,  facility_fee:540,  anesthesia_fee:935,  implant_cost:600,  total_billed:2460, payer_allowed:2265, patient_deductible_remaining:0,    patient_coinsurance:0,  patient_copay:0,  patient_total_oop:0,    insurance_pays:2265, created_at:dAgo(10), quote_sent:false },
  { id:"CE010", patient_id:"P005", provider:"Dr. R. Walker", procedure_name:"Office E&M, Initial Evaluation",                    cpt_codes:[{code:"99213",desc:"Office visit, low complexity",rvu:1.30}],               facility_type:"asc",                facility_name:"",                             payer:"Cigna",                      fee_schedule_type:"commercial_cigna", professional_fee:60,   facility_fee:0,    anesthesia_fee:0,    implant_cost:0,    total_billed:60,   payer_allowed:48,   patient_deductible_remaining:2000, patient_coinsurance:20, patient_copay:35, patient_total_oop:48,   insurance_pays:0,    created_at:dAgo(25), quote_sent:false },
];

// ─── CALCULATE ────────────────────────────────────────────────────────────────
function calculateEstimate(form) {
  const totalRVU = form.selectedCpts.reduce((s, c) => s + (Number(c.rvu) || 0), 0);
  const medicareProfFee = totalRVU * MEDICARE_CF;
  const schedMult = SCHEDULE_MULT[form.feeSchedule] || 1.20;
  const profAllowed = medicareProfFee * schedMult;
  const profBilled  = form.feeSchedule === "self_pay" ? profAllowed : profAllowed * 1.25;

  const facilMult    = FACILITY_MULT[form.facilityType] || 0.85;
  const facilAllowed = profAllowed * facilMult;
  const facilBilled  = facilAllowed * 1.30;

  let anesAllowed = 0, anesBilled = 0;
  if (form.hasAnesthesia) {
    const timeUnits  = Math.ceil(Number(form.procedureDuration) / 15);
    const totalUnits = Number(form.baseUnits) + timeUnits;
    anesAllowed = totalUnits * Number(form.anesthesiaRate);
    anesBilled  = anesAllowed * 1.30;
  }

  const implantCost    = form.hasImplant ? Number(form.implantCost) : 0;
  const noPatientImplant = ["workers_comp","pi_lien"].includes(form.feeSchedule);
  const implantCovered = noPatientImplant ? implantCost : implantCost * 0.80;
  const implantPatient = implantCost - implantCovered;

  const totalBilled  = profBilled + facilBilled + anesBilled + implantCost;
  const payerAllowed = profAllowed + facilAllowed + anesAllowed + implantCovered;

  const deductRem  = Number(form.deductibleRemaining) || 0;
  const coinsPct   = Number(form.coinsurance) / 100;
  const copay      = Number(form.copay) || 0;
  const oopMax     = Number(form.oopMaxRemaining) || 999999;

  const clinicalAllowed  = profAllowed + facilAllowed + anesAllowed;
  const deductibleApplied = Math.min(deductRem, clinicalAllowed);
  const afterDeductible   = Math.max(0, clinicalAllowed - deductibleApplied);
  const coinsuranceApplied = afterDeductible * coinsPct;

  const rawOop = deductibleApplied + coinsuranceApplied + implantPatient + copay;
  const oopMaxReached = rawOop > oopMax;
  const patientOop = oopMaxReached ? oopMax : rawOop;
  const insurancePays = Math.max(0, payerAllowed - patientOop);

  // Facility comparison
  const facilityComp = Object.entries(FACILITY_MULT).map(([type, mult]) => {
    const fA = profAllowed * mult;
    const clinA = profAllowed + fA + anesAllowed;
    const dA = Math.min(deductRem, clinA);
    const ptOop = Math.min(oopMax, dA + Math.max(0, clinA - dA) * coinsPct + implantPatient + copay);
    return { type, label: FACILITY_LABEL[type], facilityAllowed: fA, totalAllowed: clinA + implantCovered, patientOop: ptOop };
  });

  // Fee schedule comparison (professional only)
  const feeComparison = Object.entries(SCHEDULE_MULT).map(([key]) => ({
    key, label: FEE_SCHEDULES[key].label, allowed: medicareProfFee * SCHEDULE_MULT[key], color: FEE_SCHEDULES[key].color,
  }));

  return {
    totalRVU, medicareProfFee,
    profAllowed, profBilled, facilAllowed, facilBilled,
    anesAllowed, anesBilled, implantCost, implantCovered, implantPatient,
    totalBilled, payerAllowed, insurancePays, patientOop,
    deductibleApplied, coinsuranceApplied, copay, oopMaxReached, oopMax,
    facilityComp, feeComparison,
  };
}

// ─── PRINT QUOTE ──────────────────────────────────────────────────────────────
function printPatientQuote(calc, form, patient) {
  const w = window.open("", "_blank");
  w.document.write(`<!DOCTYPE html><html><head><title>Cost Estimate</title>
<style>
  body{font-family:'Georgia',serif;color:#1a2a3a;max-width:680px;margin:40px auto;padding:20px;font-size:14px;line-height:1.7}
  .header{border-bottom:3px solid #0ea5a0;padding-bottom:16px;margin-bottom:24px}
  .practice{font-size:20px;font-weight:700;color:#0ea5a0;margin-bottom:2px}
  .subtitle{font-size:12px;color:#546e7a}
  h2{font-size:16px;color:#1a2a3a;margin:24px 0 8px}
  .info-grid{display:grid;grid-template-columns:auto 1fr;gap:4px 20px;font-size:13px;margin-bottom:20px}
  .info-label{font-weight:600;color:#546e7a}
  table{width:100%;border-collapse:collapse;margin:12px 0}
  th{background:#f0f4f8;padding:8px 12px;text-align:left;font-size:12px;border:1px solid #dde3ea}
  td{padding:8px 12px;border:1px solid #dde3ea;font-size:13px}
  .total-row td{font-weight:700;background:#f8fafc}
  .patient-owes{background:#fff8e6;border:2px solid #f0a500;border-radius:8px;padding:16px 20px;margin:20px 0;text-align:center}
  .big-amount{font-size:32px;font-weight:700;color:#0ea5a0}
  .disclaimer{font-size:11px;color:#6b7280;border-top:1px solid #dde3ea;margin-top:24px;padding-top:12px;font-style:italic}
  @media print{body{margin:0}}
</style>
</head><body>
<div class="header">
  <div class="practice">Desert Orthopedics Center</div>
  <div class="subtitle">Robert Walker, MD · Upper Extremity Surgery · (480) 555-0100 · 9250 E. Shea Blvd, Suite 100, Scottsdale AZ 85260</div>
</div>
<h2>Cost Estimate for Proposed Procedure</h2>
<div class="info-grid">
  <span class="info-label">Patient:</span><span>${patient?.first || ""} ${patient?.last || ""}</span>
  <span class="info-label">Date of Birth:</span><span>${patient ? formatDOB(patient.dob) : ""}</span>
  <span class="info-label">Procedure:</span><span>${form.procedureName || form.selectedCpts.map(c => c.desc).join(", ") || "—"}</span>
  <span class="info-label">CPT Code(s):</span><span>${form.selectedCpts.map(c => c.code).join(", ") || "—"}</span>
  <span class="info-label">Facility:</span><span>${form.facilityName || FACILITY_LABEL[form.facilityType]}</span>
  <span class="info-label">Insurance:</span><span>${form.payer || "—"}</span>
  <span class="info-label">Date of Estimate:</span><span>${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</span>
</div>
<h2>Cost Summary</h2>
<table>
  <thead><tr><th>Description</th><th>Billed</th><th>Allowed</th><th>Insurance Pays</th></tr></thead>
  <tbody>
    <tr><td>Professional Fee (Dr. Walker)</td><td>${fmt(calc.profBilled)}</td><td>${fmt(calc.profAllowed)}</td><td>${fmt(Math.max(0, calc.profAllowed - calc.profAllowed * (Number(form.coinsurance)/100)))}</td></tr>
    ${calc.facilAllowed > 0 ? `<tr><td>Facility Fee (${FACILITY_LABEL[form.facilityType]})</td><td>${fmt(calc.facilBilled)}</td><td>${fmt(calc.facilAllowed)}</td><td>—</td></tr>` : ""}
    ${calc.anesAllowed > 0 ? `<tr><td>Anesthesia</td><td>${fmt(calc.anesBilled)}</td><td>${fmt(calc.anesAllowed)}</td><td>—</td></tr>` : ""}
    ${calc.implantCost > 0 ? `<tr><td>Implants/Devices</td><td>${fmt(calc.implantCost)}</td><td>${fmt(calc.implantCovered)}</td><td>—</td></tr>` : ""}
    <tr class="total-row"><td>TOTAL</td><td>${fmt(calc.totalBilled)}</td><td>${fmt(calc.payerAllowed)}</td><td>~${fmt(calc.insurancePays)}</td></tr>
  </tbody>
</table>
<div class="patient-owes">
  <div style="font-size:13px;color:#546e7a;margin-bottom:4px">YOUR ESTIMATED OUT-OF-POCKET</div>
  <div class="big-amount">${fmt(calc.patientOop)}</div>
  <div style="font-size:12px;margin-top:8px;color:#546e7a">
    Deductible applied: ${fmt(calc.deductibleApplied)} &nbsp;|&nbsp;
    Coinsurance (${form.coinsurance}%): ${fmt(calc.coinsuranceApplied)} &nbsp;|&nbsp;
    Copay: ${fmt(calc.copay)}
    ${calc.implantPatient > 0 ? `&nbsp;|&nbsp; Implant share: ${fmt(calc.implantPatient)}` : ""}
    ${calc.oopMaxReached ? `<br><strong>Out-of-pocket maximum reached — your liability is capped.</strong>` : ""}
  </div>
</div>
<p><strong>${form.payer || "Your insurance"}</strong> will pay approximately <strong>${fmt(calc.insurancePays)}</strong> of the allowed amount.</p>
<div class="disclaimer">This is an estimate only. Actual costs may vary based on final procedure coding, implant selection, insurance eligibility at time of service, and insurance adjudication. This estimate does not constitute a guarantee of coverage or payment. Please verify your benefits with your insurance carrier. For questions, contact our billing office at (480) 555-0100.</div>
</body></html>`);
  w.document.close();
  w.print();
}

// ─── ESTIMATE BUILDER ─────────────────────────────────────────────────────────
function EstimateBuilder({ form, setForm, patient, onCalculate }) {
  const [cptSearch, setCptSearch] = useState("");
  const filteredCpts = CPT_LIBRARY.filter(c =>
    cptSearch && (c.code.includes(cptSearch) || c.desc.toLowerCase().includes(cptSearch.toLowerCase())) &&
    !form.selectedCpts.find(s => s.code === c.code)
  );
  const addCpt = (c) => { setForm(f => ({ ...f, selectedCpts: [...f.selectedCpts, c] })); setCptSearch(""); };
  const removeCpt = (code) => setForm(f => ({ ...f, selectedCpts: f.selectedCpts.filter(c => c.code !== code) }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const totalRVU = form.selectedCpts.reduce((s, c) => s + c.rvu, 0).toFixed(2);

  return (
    <div style={{ width: "45%", flexShrink: 0, overflowY: "auto", padding: "16px 20px", borderRight: "1px solid var(--border)" }}>

      {/* Insurance */}
      <div style={{ background: "var(--navy-3)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>Patient Insurance</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <div className="form-label">Payer</div>
            <input className="input" value={form.payer} onChange={e => { set("payer", e.target.value); set("feeSchedule", payerToSchedule(e.target.value)); }} style={{ fontSize: 12 }} />
          </div>
          <div>
            <div className="form-label">Deductible Remaining</div>
            <input className="input" type="number" value={form.deductibleRemaining} onChange={e => set("deductibleRemaining", e.target.value)} style={{ fontSize: 12 }} />
          </div>
          <div>
            <div className="form-label">Coinsurance %</div>
            <input className="input" type="number" value={form.coinsurance} onChange={e => set("coinsurance", e.target.value)} style={{ fontSize: 12 }} />
          </div>
          <div>
            <div className="form-label">Copay</div>
            <input className="input" type="number" value={form.copay} onChange={e => set("copay", e.target.value)} style={{ fontSize: 12 }} />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <div className="form-label">OOP Max Remaining</div>
            <input className="input" type="number" value={form.oopMaxRemaining} onChange={e => set("oopMaxRemaining", e.target.value)} style={{ fontSize: 12 }} />
          </div>
        </div>
      </div>

      {/* Procedure + CPTs */}
      <div style={{ marginBottom: 16 }}>
        <div className="form-label">Procedure Name</div>
        <input className="input" value={form.procedureName} onChange={e => set("procedureName", e.target.value)} placeholder="e.g. Rotator Cuff Repair, Right Shoulder" style={{ marginBottom: 8 }} />
        <div className="form-label">CPT Codes</div>
        <div style={{ position: "relative" }}>
          <input className="input" value={cptSearch} onChange={e => setCptSearch(e.target.value)} placeholder="Search CPT codes..." style={{ fontSize: 12 }} />
          {filteredCpts.length > 0 && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--navy-3)", border: "1px solid var(--border-2)", borderRadius: 8, zIndex: 50, maxHeight: 200, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
              {filteredCpts.map(c => (
                <div key={c.code} onClick={() => addCpt(c)} style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid var(--border)", fontSize: 12 }} className="tr-clickable">
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 600, color: "var(--teal)" }}>{c.code}</span>
                    <span style={{ fontSize: 10, color: "var(--text-3)" }}>{c.rvu} RVU</span>
                  </div>
                  <div style={{ color: "var(--text-2)" }}>{c.desc}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {form.selectedCpts.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {form.selectedCpts.map(c => (
              <div key={c.code} style={{ background: "var(--teal-lt)", border: "1px solid var(--teal-md)", borderRadius: 6, padding: "4px 8px", fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 600, color: "var(--teal)" }}>{c.code}</span>
                <span style={{ color: "var(--text-2)" }}>{c.desc.slice(0, 28)}</span>
                <span style={{ color: "var(--text-3)" }}>{c.rvu} RVU</span>
                <span onClick={() => removeCpt(c.code)} style={{ cursor: "pointer", color: "var(--text-3)", marginLeft: 2 }}>✕</span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: "var(--teal)", padding: "4px 8px", background: "var(--teal-lt)", borderRadius: 6, border: "1px solid var(--teal-md)" }}>Total: {totalRVU} RVU</div>
          </div>
        )}
      </div>

      {/* Facility */}
      <div style={{ marginBottom: 16 }}>
        <div className="form-label">Facility Type</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {Object.entries(FACILITY_LABEL).map(([k, l]) => (
            <button key={k} onClick={() => { set("facilityType", k); set("facilityName", FACILITIES[k]?.[0] || ""); }} className={`btn btn-sm ${form.facilityType === k ? "btn-secondary" : "btn-ghost"}`} style={{ fontSize: 11 }}>{l}</button>
          ))}
        </div>
        <div className="form-label">Facility Name</div>
        <select className="input" value={form.facilityName} onChange={e => set("facilityName", e.target.value)} style={{ fontSize: 12 }}>
          {(FACILITIES[form.facilityType] || []).map(f => <option key={f}>{f}</option>)}
        </select>
      </div>

      {/* Fee Schedule */}
      <div style={{ marginBottom: 16 }}>
        <div className="form-label">Fee Schedule</div>
        <select className="input" value={form.feeSchedule} onChange={e => set("feeSchedule", e.target.value)} style={{ fontSize: 12 }}>
          {Object.entries(FEE_SCHEDULES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Implants */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 8 }}>
          <input type="checkbox" checked={form.hasImplant} onChange={e => set("hasImplant", e.target.checked)} />
          <span style={{ fontSize: 13, fontWeight: 500 }}>Implants / Devices Required</span>
        </label>
        {form.hasImplant && (
          <div>
            <select className="input" value={form.implantType} onChange={e => { const imp = IMPLANTS.find(i => i.name === e.target.value); set("implantType", e.target.value); if (imp && imp.cost > 0) set("implantCost", imp.cost); }} style={{ fontSize: 12, marginBottom: 8 }}>
              {IMPLANTS.map(i => <option key={i.name} value={i.name}>{i.name}{i.cost > 0 ? ` — ~${fmt(i.cost)}` : i.note ? ` (${i.note})` : ""}</option>)}
            </select>
            <div className="form-label">Implant Cost</div>
            <input className="input" type="number" value={form.implantCost} onChange={e => set("implantCost", e.target.value)} style={{ fontSize: 12 }} />
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>{IMPLANTS.find(i => i.name === form.implantType)?.note || ""}</div>
          </div>
        )}
      </div>

      {/* Anesthesia */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 8 }}>
          <input type="checkbox" checked={form.hasAnesthesia} onChange={e => set("hasAnesthesia", e.target.checked)} />
          <span style={{ fontSize: 13, fontWeight: 500 }}>Include Anesthesia Estimate</span>
        </label>
        {form.hasAnesthesia && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div>
                <div className="form-label">Anesthesia Type</div>
                <select className="input" value={form.anesthesiaType} onChange={e => set("anesthesiaType", e.target.value)} style={{ fontSize: 12 }}>
                  {["general","regional","mac","local"].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <div className="form-label">Base Units</div>
                <input className="input" type="number" value={form.baseUnits} onChange={e => set("baseUnits", e.target.value)} style={{ fontSize: 12 }} />
              </div>
            </div>
            <div className="form-label">Estimated Duration: {form.procedureDuration} min ({Math.ceil(form.procedureDuration/15)} time units)</div>
            <input type="range" min={15} max={240} step={15} value={form.procedureDuration} onChange={e => set("procedureDuration", e.target.value)} style={{ width: "100%", marginBottom: 8, accentColor: "var(--teal)" }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <div className="form-label">Rate / Unit ($)</div>
                <input className="input" type="number" value={form.anesthesiaRate} onChange={e => set("anesthesiaRate", e.target.value)} style={{ fontSize: 12 }} />
              </div>
              <div>
                <div className="form-label">Estimated Fee</div>
                <div style={{ padding: "8px 12px", background: "var(--teal-lt)", border: "1px solid var(--teal-md)", borderRadius: 6, fontSize: 13, fontWeight: 600, color: "var(--teal)" }}>
                  {fmt((Number(form.baseUnits) + Math.ceil(Number(form.procedureDuration)/15)) * Number(form.anesthesiaRate))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", fontSize: 14, padding: "10px" }} onClick={() => onCalculate(calculateEstimate(form))}>
        Calculate Estimate
      </button>
    </div>
  );
}

// ─── COST BREAKDOWN ───────────────────────────────────────────────────────────
function CostBreakdown({ calc, form, patient, onSave }) {
  if (!calc) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🧮</div>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>No estimate yet</div>
        <div style={{ fontSize: 12 }}>Complete the form and click "Calculate Estimate"</div>
      </div>
    </div>
  );

  const minOopFacility = calc.facilityComp.reduce((a, b) => b.patientOop < a.patientOop ? b : a, calc.facilityComp[0]);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

      {/* 4 Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Total Billed",     value: calc.totalBilled,   cls: "",             sub: "Chargemaster" },
          { label: "Payer Allowed",    value: calc.payerAllowed,  cls: "stat-teal",    sub: FEE_SCHEDULES[form.feeSchedule]?.label },
          { label: "Insurance Pays",   value: calc.insurancePays, cls: "",             sub: "Estimated" },
          { label: "Patient Owes",     value: calc.patientOop,    cls: calc.patientOop > 1000 ? "stat-amber" : "stat-teal", sub: calc.oopMaxReached ? "OOP max reached" : "Out of pocket" },
        ].map(s => (
          <div key={s.label} className={`stat-card ${s.cls}`}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ fontSize: 18 }}>{fmt(s.value)}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Detailed breakdown table */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Cost Breakdown</div>
        <div style={{ background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--navy-3)" }}>
                {["Service","Billed","Allowed","Ins. Pays (est.)"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "var(--text-3)", fontWeight: 500, borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["Professional Fee (Dr. Walker)", calc.profBilled, calc.profAllowed],
                ...(calc.facilAllowed > 0 ? [[`Facility Fee (${FACILITY_LABEL[form.facilityType]})`, calc.facilBilled, calc.facilAllowed]] : []),
                ...(calc.anesAllowed > 0 ? [["Anesthesia", calc.anesBilled, calc.anesAllowed]] : []),
                ...(calc.implantCost > 0 ? [["Implants/Devices", calc.implantCost, calc.implantCovered]] : []),
              ].map(([label, billed, allowed]) => (
                <tr key={label} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: "var(--text-2)" }}>{label}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, fontFamily: "var(--mono)" }}>{fmt(billed)}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, fontFamily: "var(--mono)", color: "var(--teal)" }}>{fmt(allowed)}</td>
                  <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text-3)", fontFamily: "var(--mono)" }}>—</td>
                </tr>
              ))}
              <tr style={{ background: "var(--navy-3)", borderTop: "2px solid var(--border)" }}>
                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700 }}>TOTAL</td>
                <td style={{ padding: "10px 12px", fontWeight: 700, fontFamily: "var(--mono)" }}>{fmt(calc.totalBilled)}</td>
                <td style={{ padding: "10px 12px", fontWeight: 700, fontFamily: "var(--mono)", color: "var(--teal)" }}>{fmt(calc.payerAllowed)}</td>
                <td style={{ padding: "10px 12px", fontWeight: 700, fontFamily: "var(--mono)", color: "var(--green)" }}>{fmt(calc.insurancePays)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Patient OOP breakdown */}
      <div style={{ background: "var(--amber-lt)", border: "1px solid var(--amber)", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--amber)", marginBottom: 10 }}>Patient Out-of-Pocket Breakdown</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 20px", fontSize: 12 }}>
          <span style={{ color: "var(--text-3)" }}>Deductible applied</span><span style={{ fontFamily: "var(--mono)", fontWeight: 500 }}>{fmt(calc.deductibleApplied)}</span>
          <span style={{ color: "var(--text-3)" }}>Coinsurance ({form.coinsurance}%)</span><span style={{ fontFamily: "var(--mono)", fontWeight: 500 }}>{fmt(calc.coinsuranceApplied)}</span>
          {calc.implantPatient > 0 && <><span style={{ color: "var(--text-3)" }}>Implant share</span><span style={{ fontFamily: "var(--mono)", fontWeight: 500 }}>{fmt(calc.implantPatient)}</span></>}
          <span style={{ color: "var(--text-3)" }}>Copay</span><span style={{ fontFamily: "var(--mono)", fontWeight: 500 }}>{fmt(calc.copay)}</span>
        </div>
        <div style={{ borderTop: "1px solid var(--amber)", marginTop: 10, paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Total Patient Responsibility</span>
          <span style={{ fontSize: 20, fontWeight: 700, color: "var(--amber)", fontFamily: "var(--mono)" }}>{fmt(calc.patientOop)}</span>
        </div>
        {calc.oopMaxReached && (
          <div style={{ marginTop: 8, padding: "6px 10px", background: "var(--navy-4)", borderRadius: 6, fontSize: 12, color: "var(--green)" }}>
            ✓ OOP maximum reached — patient pays $0 above this amount
          </div>
        )}
      </div>

      {/* Facility comparison */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Facility Cost Comparison</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {calc.facilityComp.map(f => (
            <div key={f.type} style={{ background: f.type === minOopFacility.type ? "var(--teal-lt)" : "var(--navy-2)", border: `1px solid ${f.type === minOopFacility.type ? "var(--teal)" : "var(--border)"}`, borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: f.type === minOopFacility.type ? "var(--teal)" : "var(--text-2)" }}>{f.label}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 2 }}>Payer Allowed</div>
              <div style={{ fontSize: 13, fontFamily: "var(--mono)", marginBottom: 6 }}>{fmt(f.totalAllowed)}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 2 }}>Patient OOP</div>
              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "var(--mono)", color: f.type === minOopFacility.type ? "var(--teal)" : "var(--text-1)" }}>{fmt(f.patientOop)}</div>
              {f.type === minOopFacility.type && <span className="badge badge-green" style={{ fontSize: 9, marginTop: 6 }}>Lower cost for patient</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Fee schedule comparison */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Professional Fee — Allowed by Payer</div>
        {calc.totalRVU > 0 ? (
          <div style={{ background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
            {calc.feeComparison.filter(f => f.key !== "self_pay").map((f, i) => (
              <div key={f.key} style={{ display: "flex", alignItems: "center", padding: "8px 14px", borderBottom: i < calc.feeComparison.length - 2 ? "1px solid var(--border)" : "none", background: f.key === form.feeSchedule ? "var(--teal-lt)" : "transparent" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: f.color, marginRight: 10, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 12, fontWeight: f.key === form.feeSchedule ? 600 : 400, color: f.key === form.feeSchedule ? "var(--teal)" : "var(--text-2)" }}>{f.label}</div>
                <div style={{ fontFamily: "var(--mono)", fontSize: 13, fontWeight: 500 }}>{fmt(f.allowed)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: "var(--text-3)", fontSize: 12 }}>Add CPT codes to see fee schedule comparison</div>
        )}
      </div>

      {/* Workers Comp / PI section */}
      {(form.feeSchedule === "workers_comp" || form.feeSchedule === "pi_lien") && (
        <div style={{ background: "var(--amber-lt)", border: "1px solid var(--amber)", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--amber)", marginBottom: 8 }}>
            {form.feeSchedule === "workers_comp" ? "Workers Compensation (AZ)" : "Personal Injury Lien"}
          </div>
          {form.feeSchedule === "workers_comp" && (
            <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.7 }}>
              <div>AZ Industrial Commission fee schedule: professional at <strong>115% of Medicare</strong></div>
              <div>Medicare equivalent: {fmt(calc.medicareProfFee)} → WC rate: {fmt(calc.profAllowed)}</div>
              <div style={{ marginTop: 6, color: "var(--text-3)" }}>Patient cost-sharing: $0 (WC covers 100% of allowed)</div>
            </div>
          )}
          {form.feeSchedule === "pi_lien" && (
            <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.7 }}>
              <div>Estimated lien value at <strong>140% of Medicare</strong></div>
              <div>Medicare ref: {fmt(calc.medicareProfFee)} → Lien: {fmt(calc.profAllowed)}</div>
              <div>Lien-to-total-billed ratio: <strong>{((calc.profAllowed / Math.max(calc.profBilled, 1)) * 100).toFixed(0)}%</strong></div>
              <div style={{ marginTop: 6, color: "var(--amber)", fontStyle: "italic" }}>Subject to negotiation at settlement</div>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-secondary" style={{ flex: 1, justifyContent: "center" }} onClick={() => printPatientQuote(calc, form, patient)}>🖨 Generate Patient Quote</button>
        <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center" }} onClick={() => onSave && onSave(calc, form)}>💾 Save Estimate</button>
      </div>
    </div>
  );
}

// ─── COST ESTIMATE TAB ────────────────────────────────────────────────────────
export function CostEstimateTab({ patient }) {
  const [form, setForm] = useState(getDefaultForm(patient));
  const [calc, setCalc] = useState(null);
  const [saved, setSaved] = useState(false);

  const handleSave = async (calcResult, f) => {
    const rec = {
      patient_id: patient?.id, provider: "Dr. R. Walker",
      procedure_name: f.procedureName || f.selectedCpts.map(c => c.desc).join(", "),
      cpt_codes: f.selectedCpts, facility_type: f.facilityType, facility_name: f.facilityName,
      payer: f.payer, fee_schedule_type: f.feeSchedule,
      professional_fee: calcResult.profAllowed, facility_fee: calcResult.facilAllowed,
      anesthesia_fee: calcResult.anesAllowed, implant_cost: calcResult.implantCost,
      total_billed: calcResult.totalBilled, payer_allowed: calcResult.payerAllowed,
      patient_deductible_remaining: f.deductibleRemaining, patient_coinsurance: f.coinsurance,
      patient_copay: f.copay, patient_total_oop: calcResult.patientOop,
      insurance_pays: calcResult.insurancePays, notes: "",
    };
    try { await db.from("cost_estimates").insert([rec]); } catch {}
    setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <EstimateBuilder form={form} setForm={setForm} patient={patient} onCalculate={setCalc} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
        {saved && (
          <div style={{ position: "absolute", top: 12, right: 12, background: "var(--green)", color: "#fff", padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 10 }}>
            ✓ Estimate saved
          </div>
        )}
        <CostBreakdown calc={calc} form={form} patient={patient} onSave={handleSave} />
      </div>
    </div>
  );
}

// ─── COST ESTIMATE PAGE ───────────────────────────────────────────────────────
export default function CostEstimatePage({ patients }) {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [estimates, setEstimates] = useState(isDemoMode ? MOCK_COST_ESTIMATES : []);
  const [loadedEst, setLoadedEst] = useState(null);

  useEffect(() => {
    if (isDemoMode) return;
    db.from("cost_estimates").select("*").order("created_at", { ascending: false })
      .then(({ data }) => setEstimates(data || []));
  }, []);

  const filteredPt = patients?.filter(p =>
    patientSearch && (`${p.first} ${p.last}`.toLowerCase().includes(patientSearch.toLowerCase()) || p.mrn.includes(patientSearch))
  ) || [];

  const [form, setForm] = useState(getDefaultForm(null));
  const [calc, setCalc] = useState(null);
  const [saved, setSaved] = useState(false);

  const selectPatient = (p) => {
    setSelectedPatient(p);
    setPatientSearch("");
    const newForm = getDefaultForm(p);
    if (loadedEst) {
      setForm({ ...newForm, ...loadedEstToForm(loadedEst) });
    } else {
      setForm(newForm);
    }
  };

  const loadEstimate = (est) => {
    const pt = patients?.find(p => p.id === est.patient_id);
    setSelectedPatient(pt || null);
    setForm({
      ...getDefaultForm(pt || null),
      procedureName: est.procedure_name, selectedCpts: est.cpt_codes || [],
      facilityType: est.facility_type, facilityName: est.facility_name,
      feeSchedule: est.fee_schedule_type,
      hasImplant: est.implant_cost > 0, implantCost: est.implant_cost, implantType: "Custom",
      hasAnesthesia: est.anesthesia_fee > 0,
      baseUnits: 7, procedureDuration: 60, anesthesiaRate: 85,
      deductibleRemaining: est.patient_deductible_remaining, coinsurance: est.patient_coinsurance,
      copay: est.patient_copay, oopMaxRemaining: 5000, payer: est.payer,
    });
    setCalc(null);
    setLoadedEst(est);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async (calcResult, f) => {
    const rec = {
      id: "CE" + Date.now(), patient_id: selectedPatient?.id,
      provider: "Dr. R. Walker",
      procedure_name: f.procedureName || f.selectedCpts.map(c => c.desc).join(", "),
      cpt_codes: f.selectedCpts, facility_type: f.facilityType, facility_name: f.facilityName,
      payer: f.payer, fee_schedule_type: f.feeSchedule,
      professional_fee: calcResult.profAllowed, facility_fee: calcResult.facilAllowed,
      anesthesia_fee: calcResult.anesAllowed, implant_cost: calcResult.implantCost,
      total_billed: calcResult.totalBilled, payer_allowed: calcResult.payerAllowed,
      patient_deductible_remaining: f.deductibleRemaining, patient_coinsurance: f.coinsurance,
      patient_copay: f.copay, patient_total_oop: calcResult.patientOop,
      insurance_pays: calcResult.insurancePays, created_at: new Date().toISOString(), quote_sent: false,
    };
    setEstimates(prev => [rec, ...prev]);
    try { await db.from("cost_estimates").insert([rec]); } catch {}
    setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  const patientName = (pid) => {
    const pt = patients?.find(p => p.id === pid);
    return pt ? `${pt.first} ${pt.last}` : pid || "—";
  };

  return (
    <div className="slide-in" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>🧮 Surgery Cost Calculator</div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>Build cost estimates, generate patient quotes, compare fee schedules</div>
        </div>
      </div>

      {/* Patient selector */}
      <div style={{ background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 8, padding: "14px 16px", marginBottom: 16, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <div className="form-label">Patient</div>
            <input className="input" placeholder="Search patient by name or MRN..." value={selectedPatient ? `${selectedPatient.first} ${selectedPatient.last} · ${selectedPatient.mrn}` : patientSearch}
              onChange={e => { if (selectedPatient) { setSelectedPatient(null); setPatientSearch(e.target.value); } else setPatientSearch(e.target.value); }}
              style={{ fontSize: 13 }}
            />
            {filteredPt.length > 0 && (
              <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--navy-3)", border: "1px solid var(--border-2)", borderRadius: 8, zIndex: 50, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", maxHeight: 200, overflowY: "auto" }}>
                {filteredPt.map(p => (
                  <div key={p.id} onClick={() => selectPatient(p)} style={{ padding: "9px 14px", cursor: "pointer", borderBottom: "1px solid var(--border)", fontSize: 13 }} className="tr-clickable">
                    <span style={{ fontWeight: 500 }}>{p.first} {p.last}</span>
                    <span style={{ color: "var(--text-3)", marginLeft: 8, fontSize: 11 }}>{p.mrn} · {p.insurance}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedPatient && (
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-2)" }}>
              <div><span style={{ color: "var(--text-3)" }}>Insurance: </span>{selectedPatient.insurance_name}</div>
              <div><span style={{ color: "var(--text-3)" }}>DOB: </span>{formatDOB(selectedPatient.dob)}</div>
              <div><span style={{ color: "var(--text-3)" }}>Age: </span>{calcAge(selectedPatient.dob)}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedPatient(null); setPatientSearch(""); setForm(getDefaultForm(null)); setCalc(null); }}>Clear</button>
            </div>
          )}
        </div>
      </div>

      {/* Estimate builder */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", border: "1px solid var(--border)", borderRadius: 8, marginBottom: 20, position: "relative" }}>
        {saved && (
          <div style={{ position: "absolute", top: 12, right: 12, background: "var(--green)", color: "#fff", padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 10 }}>
            ✓ Estimate saved
          </div>
        )}
        <EstimateBuilder form={form} setForm={setForm} patient={selectedPatient} onCalculate={setCalc} />
        <CostBreakdown calc={calc} form={form} patient={selectedPatient} onSave={handleSave} />
      </div>

      {/* Recent estimates */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Recent Estimates ({estimates.length})</div>
        <div style={{ background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--navy-3)" }}>
                {["Patient","Procedure","Payer","Total Billed","Patient OOP","Facility","Created",""].map(h => (
                  <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 11, color: "var(--text-3)", fontWeight: 500, borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {estimates.map(e => (
                <tr key={e.id} style={{ borderBottom: "1px solid var(--border)" }} className="tr-clickable">
                  <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 500 }}>{patientName(e.patient_id)}</td>
                  <td style={{ padding: "9px 12px", fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text-2)" }}>{e.procedure_name}</td>
                  <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text-2)" }}>{e.payer}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, fontFamily: "var(--mono)" }}>{fmt(e.total_billed)}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, fontFamily: "var(--mono)", color: "var(--amber)", fontWeight: 600 }}>{fmt(e.patient_total_oop)}</td>
                  <td style={{ padding: "9px 12px", fontSize: 11 }}><span className="badge badge-gray">{FACILITY_LABEL[e.facility_type] || e.facility_type}</span></td>
                  <td style={{ padding: "9px 12px", fontSize: 11, color: "var(--text-3)", fontFamily: "var(--mono)" }}>{fmtDate(e.created_at)}</td>
                  <td style={{ padding: "9px 12px" }}>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => loadEstimate(e)}>Load</button>
                  </td>
                </tr>
              ))}
              {estimates.length === 0 && <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: "var(--text-3)" }}>No saved estimates</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
